import { CopyToClipboardCell, Row } from '@library/components/table';
import {
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import {
  dateDisplay,
  dateOfBirthDisplay
} from '@bo-utils/members/memberCommon';
import { useCallback, useMemo } from 'react';
import type { FormattedMember } from '@library/types/members';
import { Tooltip } from '@library/components/tooltip/Tooltip';

const MemberGroupsCell = ({ groups }: { groups: Array<string> }) => {
  if (!groups?.length) {
    return 'None';
  }

  const groupDisplay = groups[0];
  const groupTooltip =
    groups.length > 1 ? groups.slice(1).map((name) => name) : undefined;

  return (
    <>
      <span className="mr-3 capitalize">{groupDisplay}</span>
      {groupTooltip && (
        <Tooltip text={groupTooltip} className="capitalize">
          <span className="bg-soFresh-130 text-soClean-130 rounded px-2 py-1 capitalize leading-tight">
            +{groups.length - 1}
          </span>
        </Tooltip>
      )}
    </>
  );
};

export interface MembersRowProps {
  member: FormattedMember;
  onRowClick: (id: string) => void;
}

export const MembersRow = ({ member, onRowClick }: MembersRowProps) => {
  const { dateOfBirth, email, fullName, id, joinDate, rulesets } = member;

  const copiedDOB = useMemo(
    () => dateOfBirthDisplay(String(dateOfBirth)),
    [dateOfBirth]
  );

  const handleRowClick = useCallback(() => onRowClick(id), [id, onRowClick]);

  return (
    <Row
      aria-hidden="true"
      dataTestId={`member-${id}`}
      onClickRow={handleRowClick}
      rowClassName="cursor-pointer group/row"
    >
      <PlayerProfile
        className="basis-96 px-3"
        name={fullName}
        subheader={email}
      >
        <div className="flex flex-col">
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium capitalize tracking-tight" />
          <PlayerSubheader className="text-soClean-170 font-heebo block text-xs font-medium tracking-tight" />
        </div>
      </PlayerProfile>
      <CopyToClipboardCell
        dataTestId={`member-${id}-id`}
        label={id}
        textCopied={id}
      />
      <p className="flex flex-1 p-3" data-testid={`member-${id}-join-date`}>
        {dateDisplay(joinDate)}
      </p>
      <CopyToClipboardCell
        dataSentryMask
        dataTestId={`member-${id}-dob`}
        label={dateDisplay(String(dateOfBirth))}
        textCopied={copiedDOB}
      />
      <div
        className="flex flex-1 flex-wrap items-center p-3"
        data-testid={`member-${id}-member-groups`}
      >
        <MemberGroupsCell groups={rulesets} />
      </div>
    </Row>
  );
};
